// DeliveryNoteDialog.jsx
import React, { useEffect, useMemo, useState, useCallback } from 'react';
import { Dialog } from 'primereact/dialog';
import { Button } from 'primereact/button';
import DeliveryNoteForm from './DeliveryNoteForm';
import OrderPickupTablePage from './OrderPickupTable';
import {
  useGetDeliveryNotePickupDetailQuery,
  useDeliveryNotePickupMutation,
} from '../../../../api/endpoints/parts';
import { toastService } from '../../../../utils/toastService';
import { useLanguage } from '../../../../contexts/LanguageContext';

const emptyHeader = {
  invoiceNo: '',
  invoiceDate: null,
  totalAmount: '-',
  totalSubAmount: '-',
  totalDiscount: '-',
};

export default function DeliveryNoteDialog({ visible, onHide, deliveryNoteNo }) {
  const [headerInfo, setHeaderInfo] = useState(emptyHeader);
  const [partData, setPartData] = useState([]);
  const [loaded, setLoaded] = useState(false);
  const { th } = useLanguage();

  const { data, refetch, isFetching } = useGetDeliveryNotePickupDetailQuery(
    { DeliveryNoteNumber: deliveryNoteNo ?? '' },
    { skip: !visible || !deliveryNoteNo, refetchOnMountOrArgChange: true }
  );

  const [createPickup, { isLoading: isSaving }] = useDeliveryNotePickupMutation();

  const mapApiToRows = useCallback((d) => {
    const rows = (d?.parts ?? []).map((p, i) => {
      const totalQty = p.totalQuantity ?? p.quantity ?? 0;
      const receivedQty = p.receivedQuantity ?? 0;
      const unitDiscount = (p.discount ?? 0) / ((p.quantity ?? 1) || 1);
      const amount = p.amount;

      return {
        id: i + 1,
        partCode: p.partCode,
        partDescreption: p.partName,
        unitPrice: p.unitPrice ?? 0,
        unit: p.mein,
        totalQuantity: totalQty,
        receivedQuantity: receivedQty,
        quantity: 0,
        // warehouseId tablo component'i içinde default ilk id olarak set edilecek
        warehouseId: null,
        amount,
        _unitDiscount: unitDiscount,
        ordrNo: p.ordrN ?? '',
        psnr: p.psnr ?? '',
        dln: p.dln ?? '',
        shelf: p.shelf ?? '',
        eyes: p.eyes ?? '',
      };
    });
    return rows;
  }, []);

  useEffect(() => {
    if (visible && deliveryNoteNo) refetch();
  }, [visible, deliveryNoteNo, refetch]);

  useEffect(() => {
    if (!visible) return;

    const d = data?.data;
    if (d) {
      setHeaderInfo({
        invoiceNo: d.invoiceNo ?? '',
        invoiceDate: d.invoiceDate ? new Date(d.invoiceDate) : null,
        totalAmount: d.totalAmount ?? '-',
        totalSubAmount: d.totalSubAmount ?? '-',
        totalDiscount: d.totalDiscount ?? '-',
      });
      const rows = mapApiToRows(d);
      setPartData(rows);
      setLoaded(true);
    } else {
      setHeaderInfo({ ...emptyHeader, invoiceNo: deliveryNoteNo ?? '' });
      setPartData([]);
      setLoaded(false);
    }
  }, [visible, data, deliveryNoteNo, mapApiToRows]);

  const handleSave = async () => {
    let rows = partData;

    if (!rows?.length) {
      const res = await refetch();
      const fresh = res?.data?.data;
      if (fresh) {
        rows = mapApiToRows(fresh);
        setPartData(rows);
        setHeaderInfo((prev) => ({
          ...prev,
          invoiceNo: fresh.invoiceNo ?? prev.invoiceNo,
        }));
      }
    }

    const warehouses = data?.data?.warehouses ?? [];
    const defaultWarehouseId = warehouses?.[0]?.id ?? 0;

    const payload = {
      DeliveryNoteNumber: headerInfo?.invoiceNo || deliveryNoteNo || '',
      description: '',
      parts: (rows || [])
        .filter((p) => p.partCode && (p.quantity ?? 0) > 0)
        .map((p) => ({
          partCode: p.partCode,
          warehouseId: p.warehouseId ?? defaultWarehouseId,
          quantity: Number(p.quantity) || 0,
          ordrN: p.ordrNo ?? '',
          psnr: p.psnr ?? '',
        })),
    };

    const result = await createPickup(payload).unwrap();
    if (result && result.isSuccessful) {
      toastService.success(th('warrantyClaimLimit.toastMessages.successSummary'));
      onHide();
    }
  };

  const footer = useMemo(
    () => (
      <div className="flex gap-2 justify-content-end w-full">
        <Button label={th('common.cancel')} text onClick={onHide} />
        <Button
          label={th('common.save')}
          onClick={handleSave}
          disabled={isFetching || isSaving || !deliveryNoteNo}
        />
      </div>
    ),
    [onHide, isFetching, isSaving, deliveryNoteNo, handleSave]
  );

  return (
    <Dialog
      header={th('parts.partDelivery')}
      visible={visible}
      onHide={onHide}
      style={{ width: 980, maxWidth: 980 }}
      modal
      unmountOnHide
      footer={footer}
    >
      <DeliveryNoteForm headerInfo={headerInfo} />
      <div style={{ marginTop: 24 }}>
        <OrderPickupTablePage
          tableData={partData}
          setTableData={setPartData}
          warehouses={data?.data?.warehouses ?? []}
          hidePriceColumns
          deliveryNoteNo={deliveryNoteNo}
        />
      </div>
    </Dialog>
  );
}

